import {useMutation, useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {
  CreateKycRecordApiPayloadV2,
  CreateKycRecordApiResponse,
  GetUserKycApiResponse,
  UpdateKycRecordApiPayloadV2,
} from "./types";
import {useClientV2} from "../../utils/hooks";
import {message} from "antd";
import {AnalyticsEvent} from "../../utils/analytics";

/**
 * @deprecated Use `useKycStatus` from `src/features/kyc/hooks` instead.
 * Returns KycRecord entity with proper status typing and business methods.
 * (V1 branch removed — always calls V2 `GET kyc`.)
 */
export const useGetKycStatus = (enabled = true) => {
  const client = useClientV2();
  return useQuery<GetUserKycApiResponse, AxiosError<{status: boolean; message: string}>>({
    queryKey: ["kyc-status"],
    queryFn: () => client("kyc"),
    enabled,
    retry: 0,
    refetchOnWindowFocus: false,
  });
};

/**
 * @deprecated Use `useCreateKyc` from `src/features/kyc/hooks` instead.
 * In v2 flow, use `useSubmitKyc` orchestration hook which handles upload + create.
 * (V1 branch removed — always calls V2 `POST kyc`.)
 */
export const useCreateKycRecordApi = () => {
  const client = useClientV2();
  return useMutation<
    {data: CreateKycRecordApiResponse},
    AxiosError<
      {
        message: string;
      },
      any
    >,
    CreateKycRecordApiPayloadV2,
    unknown
  >({
    mutationFn: (kycRecordData: CreateKycRecordApiPayloadV2) =>
      client("kyc", {
        method: "POST",
        data: kycRecordData,
      }),

    onError: (error: AxiosError<{message: string}>) => {
      if (error.message) {
        message.error(error.message);
      }
      AnalyticsEvent("KYC Record", "KYC Record create fail");
    },
    onSuccess: () => {
      AnalyticsEvent("KYC Record", "KYC Record create success");
    },
  });
};

/**
 * @deprecated Use `useUpdateKyc` from `src/features/kyc/hooks` instead.
 * In v2 flow, use `useSubmitKyc` orchestration hook which handles upload + update.
 * (V1 branch removed — always calls V2 `PATCH kyc`.)
 */
export const useUpdateKycRecordApi = () => {
  const client = useClientV2();
  return useMutation<
    {data: CreateKycRecordApiResponse},
    AxiosError<
      {
        message: string;
      },
      any
    >,
    UpdateKycRecordApiPayloadV2,
    unknown
  >({
    mutationFn: (kycRecordData: UpdateKycRecordApiPayloadV2) =>
      client("kyc", {
        method: "PATCH",
        data: kycRecordData,
      }),

    onError: (error: AxiosError<{message: string}>) => {
      if (error.message) {
        message.error(error.message);
      }
      AnalyticsEvent("KYC Record", "KYC Record update fail");
    },
    onSuccess: () => {
      AnalyticsEvent("KYC Record", "KYC Record update success");
    },
  });
};

/**
 * @deprecated Use `useUploadKycDocuments` from `src/features/kyc/hooks` instead.
 * In v2 flow, use `useSubmitKyc` orchestration hook which handles upload + create/update.
 * (V1 branch removed — always calls V2 `POST kyc/upload-documents`.)
 */
export const useUploadKycDocs = () => {
  const client = useClientV2();
  return useMutation({
    mutationFn: (payload: {data: FormData}) =>
      client(`kyc/upload-documents`, {
        method: "POST",
        data: payload.data,
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
      AnalyticsEvent("KYC Record", "KYC Record doc upload fail");
    },
    onSuccess: () => {
      AnalyticsEvent("KYC Record", "KYC Record doc upload success");
    },
    // onSettled: (d, e, values) =>
    //   queryClient.invalidateQueries({queryKey: ["kyc-status"]}),
  });
};

/**
 * @deprecated v1 branch removed. Prefer `KycRepositoryV2.submitForReview()` via
 * `useUploadKycDocsOnly` from `src/features/kyc/hooks`. Always calls V2 `PATCH kyc/submit-review`.
 */
export const useUpdateKycStatus = () => {
  const client = useClientV2();
  return useMutation({
    mutationFn: () =>
      client("kyc/submit-review", {
        method: "PATCH",
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
      AnalyticsEvent("KYC Record", "KYC Record status fail");
    },
    onSuccess: () => {
      AnalyticsEvent("KYC Record", "KYC Record status success");
    },
    // onSettled: (d, e, values) =>
    //   queryClient.invalidateQueries({queryKey: ["kyc-status"]}),
  });
};
